/**
 * RichTextEditor — reusable contentEditable + execCommand editor.
 *
 * Originally inlined in AdminMonthlyDigest.jsx; extracted in iter-84 so the
 * event Details box can reuse the exact same toolbar + output shape. Submitted
 * HTML is sanitised server-side by bleach, so the editor only needs to produce
 * markup — it doesn't need to trust itself.
 *
 * Props:
 *   value         — current HTML string
 *   onChange      — (html) => void; called on every input event
 *   onBlur        — () => void; called when the editor loses focus (autosave hook)
 *   placeholder   — text shown when empty (CSS-only via :empty:before)
 *   disabled      — disables both toolbar + contentEditable
 *   minHeight     — min-height of the editing area (default 120px)
 *   maxHeight     — max-height of the editing area (default 480px)
 *   compact       — smaller toolbar (used for inline boxes like event details)
 *   testId        — root data-testid (toolbar buttons + textarea will use it)
 *
 * Note: paste is stripped to plain text on purpose — keeps markup clean and
 * avoids inheriting Word/Google Docs styling.
 */
import { useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Bold, Italic, List, ListOrdered, Heading3, Link as LinkIcon, Undo2, Quote } from "lucide-react";

const Toolbar = ({ exec, compact, testIdPrefix, disabled }) => {
  const Btn = ({ cmd, arg, icon: Icon, label }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={compact ? "h-7 w-7 p-0" : "h-8 w-8 p-0"}
      title={label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(cmd, arg)}
      data-testid={`${testIdPrefix}-toolbar-${cmd}${arg ? `-${arg.replace(/\W/g, "")}` : ""}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </Button>
  );
  return (
    <div className="flex items-center gap-1 p-1.5 border-b border-white/10 bg-black/20 rounded-t-md flex-wrap">
      <Btn cmd="bold" icon={Bold} label="Bold" />
      <Btn cmd="italic" icon={Italic} label="Italic" />
      {!compact && <Btn cmd="formatBlock" arg="<h3>" icon={Heading3} label="Heading" />}
      <Btn cmd="formatBlock" arg="<blockquote>" icon={Quote} label="Quote" />
      <Btn cmd="insertUnorderedList" icon={List} label="Bullet list" />
      <Btn cmd="insertOrderedList" icon={ListOrdered} label="Numbered list" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={compact ? "h-7 w-7 p-0" : "h-8 w-8 p-0"}
        title="Insert link"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const url = window.prompt("Link URL");
          if (url) exec("createLink", url);
        }}
        data-testid={`${testIdPrefix}-toolbar-link`}
      >
        <LinkIcon className="w-3.5 h-3.5" />
      </Button>
      <Btn cmd="undo" icon={Undo2} label="Undo" />
    </div>
  );
};

export const RichTextEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  minHeight = 120,
  maxHeight = 480,
  compact = false,
  testId = "rich-text-editor",
}) => {
  const editorRef = useRef(null);

  // Sync external value into the DOM only when it differs (avoid wiping caret)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const exec = (cmd, arg) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    const html = editorRef.current?.innerHTML || "";
    onChange?.(html);
  };

  return (
    <div className="rounded-md border border-white/10 bg-black/10">
      <Toolbar exec={exec} compact={compact} testIdPrefix={testId} disabled={disabled} />
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder || ""}
        className="rte-area p-3 text-sm leading-relaxed focus:outline-none prose prose-invert prose-sm max-w-none overflow-y-auto"
        style={{ minHeight, maxHeight }}
        onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
        onBlur={() => onBlur?.()}
        onPaste={(e) => {
          // Strip pasted formatting to keep markup clean
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          document.execCommand("insertText", false, text);
        }}
        data-testid={testId}
      />
      {/* CSS for placeholder shown only when the editor is empty */}
      <style>{`
        .rte-area:empty:before {
          content: attr(data-placeholder);
          color: rgba(255,255,255,0.35);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
